<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { appStore } from "$app/store.svelte"
  import { formatDateTime, xhr, toast, wait } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"

  type defType = {
    plural: string
    singular: string
    collection: string
  }

  const modDefinitions = [
    {
      plural: "Departments",
      singular: "Department",
      collection: "departments"
    },
    {
      plural: "Strengths",
      singular: "Strength",
      collection: "strengths"
    },
    {
      plural: "Weaknesses",
      singular: "Weakness",
      collection: "weaknesses"
    },
    {
      plural: "Opportunities",
      singular: "Opportunity",
      collection: "opportunities"
    },
    {
      plural: "Threats",
      singular: "Threat",
      collection: "threats"
    }
  ]

  type modType =
    | "departments"
    | "strengths"
    | "weaknesses"
    | "opportunities"
    | "threats"
  let data: { [key in modType]: any[] } = {
    departments: [],
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: []
  }

  let isLoading = true
  async function initialize(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/settings/archive_data`
      )

      data = resp.data
    } catch (e) {
      console.log(e)
      toast("There was an error loading the archive data", "error")
    } finally {
      isLoading = false
    }
  }

  async function restoreModel(model: any, definition: defType): Promise<void> {
    const { collection, singular } = definition
    try {
      model._selected = true

      data[collection] = data[collection]

      await wait(20)
      if (
        !confirm(`Are you sure you want to restore ${singular} ${model.name}?`)
      ) {
        model._selected = false
        data[definition.collection] = data[definition.collection]
        return
      }

      await xhr.patch(`/plans/${appStore.plan.id}/archive/${model.id}`, {
        model: singular
      })

      const idx = data[collection].findIndex((m: any) => m.id === model.id)
      data[collection].splice(idx, 1)

      toast(`${singular} ${model.name} restored`, "success")
    } catch (e) {
      console.error(e)
      Honeybadger.notify(e)
      toast(`There was an error restoring the ${singular}`, "error")
    } finally {
      model._selected = false
      data[collection] = data[collection]
    }
  }

  onMount(async () => {
    initialize()
  })
</script>

<div class="max-w-4xl mx-auto py-8">
  {#if isLoading}
    <div class="py-12 flex justify-center w-full">
      <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
    </div>
  {:else}
    <div class="bg-white divide-y divide-gray-200">
      {#each modDefinitions as definition}
        {@const mods = data[definition.collection]}
        <div class="py-6">
          <h2 class="text-xl mb-4 font-bold">{definition.plural}</h2>

          {#each mods as mod}
            <div
              class="grid grid-cols-3 gap-x-4 px-4 py-2 mb-4 shadow border-blue-50 border rounded"
              class:bg-blue-50={mod._selected}
            >
              <div class="text-base font-bold flex items-center">
                {mod.name}
              </div>
              <div class="flex-row justify-center items-center">
                <div class="text-gray-500 block text-sm">Deleted on</div>
                {formatDateTime(mod.deleted_at)}
              </div>
              <div class="flex items-center justify-center">
                <button
                  class="btn btn-primary"
                  on:click={() => restoreModel(mod, definition)}
                >
                  Restore
                </button>
              </div>
              <div></div>
            </div>
          {:else}
            <div
              class="inline-block text-left px-5 py-2 mb-4 bg-yellow-50 rounded-lg text-yellow-800 border border-yellow-100 shadow-sm"
            >
              No {definition.plural} have been archived
            </div>
          {/each}
        </div>
      {/each}
    </div>
  {/if}
</div>
